import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Camera,
  Video,
  Trash2,
  Download,
  Share2,
  Image,
  Grid3X3,
  LayoutList,
  Play,
} from 'lucide-react';
import { useCapturesStore, type Capture } from '@/stores/captures-store';
import { useUIStore } from '@/stores/ui-store';
import { formatDistanceToNow, formatDuration } from '@/lib/utils';
import Button from '@/components/ui/Button';

type ViewMode = 'grid' | 'list';
type FilterType = 'all' | 'photo' | 'video';

export default function CapturesDrawer({ onClose }: { onClose: () => void }) {
  const { captures, deleteCapture, clearAllCaptures, setSelectedCapture } = useCapturesStore();
  const { showToast } = useUIStore();

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSelecting, setIsSelecting] = useState(false);

  const filteredCaptures = captures.filter((c) => {
    if (filter === 'all') return true;
    return c.type === filter;
  });

  const photoCount = captures.filter((c) => c.type === 'photo').length;
  const videoCount = captures.filter((c) => c.type === 'video').length;

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const deleteSelected = () => {
    selectedIds.forEach((id) => deleteCapture(id));
    showToast(`${selectedIds.size} capture${selectedIds.size > 1 ? 's' : ''} deleted`, 'success');
    setSelectedIds(new Set());
    setIsSelecting(false);
  };

  const downloadCapture = async (capture: Capture) => {
    try {
      const link = document.createElement('a');
      link.href = capture.dataUrl;
      link.download = `visionar-${capture.type}-${Date.now()}.${capture.type === 'photo' ? 'jpg' : 'mp4'}`;
      link.click();
      showToast('Download started', 'success');
    } catch {
      showToast('Failed to download', 'error');
    }
  };

  const shareCapture = async (capture: Capture) => {
    try {
      if (navigator.share) {
        // Convert data URL to blob for sharing
        const response = await fetch(capture.dataUrl);
        const blob = await response.blob();
        const file = new File(
          [blob],
          `visionar-${capture.type}.${capture.type === 'photo' ? 'jpg' : 'mp4'}`,
          { type: capture.type === 'photo' ? 'image/jpeg' : 'video/mp4' }
        );

        await navigator.share({
          files: [file],
          title: 'VisionAR Capture',
          text: capture.modelName ? `Check out ${capture.modelName} in AR!` : 'Check out my AR capture!',
        });
      } else {
        // Fallback: copy to clipboard notification
        showToast('Sharing not supported on this device', 'info');
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        showToast('Failed to share', 'error');
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100]"
    >
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Drawer */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="absolute bottom-0 left-0 right-0 max-h-[90vh] bg-background rounded-t-[32px] overflow-hidden flex flex-col"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center">
              <Camera size={20} className="text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold">My Captures</h2>
              <p className="text-sm text-white/50">
                {photoCount} photo{photoCount !== 1 ? 's' : ''} • {videoCount} video{videoCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-white/5">
          {/* Filter Tabs */}
          <div className="flex gap-1.5">
            {[
              { id: 'all' as const, label: 'All', count: captures.length },
              { id: 'photo' as const, label: 'Photos', count: photoCount },
              { id: 'video' as const, label: 'Videos', count: videoCount },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  filter === tab.id
                    ? 'bg-white/15 text-white'
                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* View Mode & Actions */}
          <div className="flex items-center gap-2">
            {isSelecting ? (
              <>
                <button
                  onClick={() => {
                    setIsSelecting(false);
                    setSelectedIds(new Set());
                  }}
                  className="text-xs text-white/50 hover:text-white/70"
                >
                  Cancel
                </button>
                {selectedIds.size > 0 && (
                  <button
                    onClick={deleteSelected}
                    className="p-2 rounded-full bg-red-500/15 hover:bg-red-500/25 transition-colors"
                  >
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                )}
              </>
            ) : (
              <>
                {captures.length > 0 && (
                  <button
                    onClick={() => setIsSelecting(true)}
                    className="text-xs text-white/50 hover:text-white/70"
                  >
                    Select
                  </button>
                )}
                <button
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                  className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  {viewMode === 'grid' ? (
                    <LayoutList size={16} className="text-white/60" />
                  ) : (
                    <Grid3X3 size={16} className="text-white/60" />
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Captures Grid/List */}
        <div className="flex-1 overflow-y-auto no-scrollbar p-4">
          {filteredCaptures.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-16 px-6"
            >
              <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4">
                <Image size={32} className="text-white/30" />
              </div>
              <h3 className="font-bold text-lg mb-1">No captures yet</h3>
              <p className="text-white/50 text-center text-sm mb-6">
                Take photos and videos in AR mode to save them here.
              </p>
              <Button onClick={onClose} variant="secondary">
                Open AR Camera
              </Button>
            </motion.div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-3 gap-1.5">
              <AnimatePresence>
                {filteredCaptures.map((capture, index) => (
                  <motion.div
                    key={capture.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.02 }}
                    onClick={() => {
                      if (isSelecting) {
                        toggleSelect(capture.id);
                      } else {
                        setSelectedCapture(capture);
                      }
                    }}
                    className={`relative aspect-square rounded-xl overflow-hidden cursor-pointer group ${
                      selectedIds.has(capture.id) ? 'ring-2 ring-accent-blue' : ''
                    }`}
                  >
                    {capture.type === 'photo' ? (
                      <img
                        src={capture.dataUrl}
                        alt="Capture"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-black flex items-center justify-center">
                        <video
                          src={capture.dataUrl}
                          className="w-full h-full object-cover"
                          muted
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play size={24} className="text-white" fill="white" />
                        </div>
                      </div>
                    )}

                    {/* Selection checkbox */}
                    {isSelecting && (
                      <div
                        className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          selectedIds.has(capture.id)
                            ? 'bg-accent-blue border-accent-blue'
                            : 'border-white/50 bg-black/30'
                        }`}
                      >
                        {selectedIds.has(capture.id) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-2 h-2 bg-white rounded-full"
                          />
                        )}
                      </div>
                    )}

                    {/* Video duration badge */}
                    {capture.type === 'video' && capture.duration && (
                      <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-white font-medium">
                        {formatDuration(capture.duration)}
                      </div>
                    )}

                    {/* Hover overlay */}
                    {!isSelecting && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadCapture(capture);
                            }}
                            className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              shareCapture(capture);
                            }}
                            className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                          >
                            <Share2 size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="space-y-2">
              <AnimatePresence>
                {filteredCaptures.map((capture, index) => (
                  <motion.div
                    key={capture.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => {
                      if (isSelecting) {
                        toggleSelect(capture.id);
                      } else {
                        setSelectedCapture(capture);
                      }
                    }}
                    className={`flex gap-3 p-3 rounded-2xl bg-white/5 hover:bg-white/[0.08] cursor-pointer transition-colors ${
                      selectedIds.has(capture.id) ? 'ring-2 ring-accent-blue' : ''
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 relative">
                      {capture.type === 'photo' ? (
                        <img
                          src={capture.dataUrl}
                          alt="Capture"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <>
                          <video
                            src={capture.dataUrl}
                            className="w-full h-full object-cover"
                            muted
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Play size={16} className="text-white" fill="white" />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {capture.type === 'photo' ? (
                          <Camera size={14} className="text-green-400" />
                        ) : (
                          <Video size={14} className="text-accent-blue" />
                        )}
                        <span className="font-semibold text-sm capitalize">{capture.type}</span>
                      </div>
                      {capture.modelName && (
                        <p className="text-sm text-white/70 truncate">{capture.modelName}</p>
                      )}
                      <p className="text-xs text-white/40 mt-0.5">
                        {formatDistanceToNow(new Date(capture.createdAt))}
                        {capture.type === 'video' && capture.duration && (
                          <> • {formatDuration(capture.duration)}</>
                        )}
                      </p>
                    </div>

                    {/* Actions */}
                    {isSelecting ? (
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center self-center ${
                          selectedIds.has(capture.id)
                            ? 'bg-accent-blue border-accent-blue'
                            : 'border-white/30'
                        }`}
                      >
                        {selectedIds.has(capture.id) && (
                          <div className="w-2.5 h-2.5 bg-white rounded-full" />
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 self-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadCapture(capture);
                          }}
                          className="p-2 rounded-full hover:bg-white/10 transition-colors"
                        >
                          <Download size={16} className="text-white/50" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            shareCapture(capture);
                          }}
                          className="p-2 rounded-full hover:bg-white/10 transition-colors"
                        >
                          <Share2 size={16} className="text-white/50" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCapture(capture.id);
                            showToast('Capture deleted', 'success');
                          }}
                          className="p-2 rounded-full hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 size={16} className="text-white/50" />
                        </button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Safe Area Bottom */}
        <div className="safe-area-bottom" />
      </motion.div>
    </motion.div>
  );
}
